import { useState, useEffect } from 'react'
import { contentService } from '../utils/contentService'
import { storeInfoService, StoreInfo } from '../services/storeInfoService'
import SEO from '../components/SEO'

function BootstrapAbout() {
  const [aboutContent, setAboutContent] = useState(contentService.getAboutContent())
  const [storeInfo, setStoreInfo] = useState<StoreInfo>(storeInfoService.getStoreInfo())

  useEffect(() => {
    // Listen for content updates
    const unsubscribeContent = contentService.subscribe(() => {
      console.log('BootstrapAbout: Content updated, reloading...')
      setAboutContent(contentService.getAboutContent())
    })

    // Listen for store info updates
    const unsubscribeStore = storeInfoService.subscribe(() => {
      setStoreInfo(storeInfoService.getStoreInfo())
    })
    
    return () => {
      unsubscribeContent()
      unsubscribeStore()
    }
  }, [])
  return (
    <div className="py-5">
      <SEO
        title="About Us - Meet Our Artists & Instructors"
        description="Discover The Salty Mermaid Art Gallery & Studio in Ormond Beach, FL. Featuring 75+ local artists, 7 friendly instructors, and weekly art classes for all skill levels."
        canonicalUrl="https://saltymermaidart.com/about"
      />
      <div className="container">

        {/* Hero Section */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h1 className="display-4 fw-bold mb-4">About The Salty Mermaid</h1>
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  A stunning art gallery and studio featuring original artworks from over 75 talented local artists, 
                  plus weekly art classes with 7 friendly instructors.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Story Section */}
        <div className="row mb-5 justify-content-center">
          <div className="col-lg-8 mb-4">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <h2 className="h3 text-primary fw-bold mb-4">Our Story</h2>
                <p className="text-dark mb-4">
                  {aboutContent.story}
                </p>
                <p className="text-dark mb-0">
                  {aboutContent.mission}
                </p>
              </div>
            </div>
          </div>
          <div className="col-lg-8 mb-4">
            <div className="bg-white p-5 rounded text-center shadow-sm">
              <div className="display-1 mb-3">🏆</div>
              <h4 className="text-success mb-4">Award Winning Gallery</h4>
              <div className="mb-3">
                <p className="text-dark mb-2">
                  <strong className="text-primary">FIRST PLACE</strong><br />
                  "Best Place to Buy a Gift"
                </p>
              </div>
              <div>
                <p className="text-dark mb-1">
                  <strong className="text-primary">SECOND PLACE</strong><br />
                  "Best Woman Owned Business"
                </p>
                <p className="text-dark mb-0">
                  "Best Art Gallery"
                </p>
              </div>
              <p className="text-muted mt-3 mb-0 small">
                Central Florida Favorites BEST OF 2025 contest
              </p>
              <div className="row g-2 mt-4 justify-content-center">
                {[2026, 2025, 2024].map(year => (
                  <div className="col-4" key={year}>
                    <img
                      src={`/images/awards/businessrate-best-of-${year}.webp`}
                      alt={`BusinessRate Best of ${year} Award Winner - The Salty Mermaid Art Gallery, Ormond Beach, Florida`}
                      className="img-fluid"
                      width={400}
                      height={637}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              <p className="text-dark fw-semibold mt-3 mb-0">
                Voted number one 3 years in a row by our community through Google reviews
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Values */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="card shadow-lg border-0 mb-5" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h2 className="h3 text-primary fw-bold mb-0">Our Mission & Values</h2>
              </div>
            </div>
            <div className="row g-4">
              <div className="col-md-4">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body text-center p-4">
                    <div className="display-4 mb-3">🎨</div>
                    <h4 className="h5 fw-bold">Creativity</h4>
                    <p className="text-dark mb-0">
                      We believe everyone has an inner artist waiting to be discovered. Our classes and workshops 
                      provide a supportive environment for creative exploration.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body text-center p-4">
                    <div className="display-4 mb-3">🌊</div>
                    <h4 className="h5 fw-bold">Coastal Inspiration</h4>
                    <p className="text-dark mb-0">
                      Living by the ocean provides endless inspiration. We celebrate the colors, textures, 
                      and natural beauty of coastal Florida in our art and instruction.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body text-center p-4">
                    <div className="display-4 mb-3">🤝</div>
                    <h4 className="h5 fw-bold">Community</h4>
                    <p className="text-dark mb-0">
                      Art brings people together. We foster a welcoming community where local artists 
                      can connect, collaborate, and grow together.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="row mb-5">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h2 className="h3 text-primary fw-bold mb-2">By the Numbers</h2>
                <p className="text-dark mb-4">The impact we've made in our community</p>
                <div className="row">
                  <div className="col-md-3 col-6 mb-3">
                    <div className="display-4 text-primary fw-bold">75+</div>
                    <p className="text-dark mb-0">Local Artists Featured</p>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="display-4 text-success fw-bold">7</div>
                    <p className="text-dark mb-0">Art Instructors</p>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="display-4 fw-bold" style={{color: '#dc3545'}}>30+</div>
                    <p className="text-dark mb-0">Different Classes</p>
                  </div>
                  <div className="col-md-3 col-6 mb-3">
                    <div className="display-4 fw-bold" style={{color: '#0c76a3'}}>8</div>
                    <p className="text-dark mb-0">Years Serving</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location & Hours */}
        <div className="row mb-5">
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <h2 className="h3 text-primary fw-bold mb-4">Visit Our Studio</h2>
                
                <h5 className="card-title">📍 Location</h5>
                <p className="card-text">
                  {storeInfo.address.street}<br />
                  {storeInfo.address.city}, {storeInfo.address.state} {storeInfo.address.zipCode}<br />
                  {storeInfo.address.description && <em>{storeInfo.address.description}</em>}
                </p>
                
                <h5 className="card-title mt-4">🕒 Hours</h5>
                <div className="row">
                  {storeInfoService.getWorkingDaysHours().map((period, index) => (
                    <div key={index} className="col-sm-6">
                      <p className="card-text mb-1"><strong>{period.days}:</strong></p>
                      <p className="text-muted">{period.hours}</p>
                    </div>
                  ))}
                </div>

                <h5 className="card-title mt-4">📞 Contact</h5>
                <p className="card-text">
                  Phone: {storeInfo.contact.phone}<br />
                  Email: {storeInfo.contact.email}<br />
                  Website: {storeInfo.contact.website}
                </p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 mb-4">
            <div className="card shadow-sm border-0" style={{
              borderRadius: '15px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4">
                <h2 className="h3 text-primary fw-bold mb-4">What Makes Us Special</h2>
                <div className="list-group list-group-flush">
                  <div className="list-group-item border-0 px-0">
                    <h6 className="fw-bold text-success mb-1">🏖️ Oceanside Location</h6>
                    <p className="text-dark mb-0 small">
                      Located on Ocean Shore Blvd with beautiful oceanside views for natural inspiration.
                    </p>
                  </div>
                  <div className="list-group-item border-0 px-0">
                    <h6 className="fw-bold text-info mb-1">👩‍🎨 Seven Talented Instructors</h6>
                    <p className="text-dark mb-0 small">
                      Learn from 7 friendly and experienced art instructors offering diverse techniques and styles.
                    </p>
                  </div>
                  <div className="list-group-item border-0 px-0">
                    <h6 className="fw-bold text-warning mb-1">🎨 Diverse Art Classes</h6>
                    <p className="text-dark mb-0 small">
                      Acrylic & watercolor painting, jewelry making, resin art, mixed media, stained glass, sea glass art, and more!
                    </p>
                  </div>
                  <div className="list-group-item border-0 px-0">
                    <h6 className="fw-bold text-danger mb-1">🛍️ Gift Shop & Gallery</h6>
                    <p className="text-dark mb-0 small">
                      Home decor, jewelry, candles, pottery, wind chimes, refurbished coastal furniture, and glassware.
                    </p>
                  </div>
                  <div className="list-group-item border-0 px-0">
                    <h6 className="fw-bold text-primary mb-1">🍷 BYOB Welcome</h6>
                    <p className="text-dark mb-0 small">
                      Bring your own beverages to art classes and private parties are encouraged for a fun, relaxed atmosphere.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="row">
          <div className="col-12">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-5 text-center">
                <h3 className="text-primary fw-bold mb-3">Ready to Join Our Creative Community?</h3>
                <p className="text-dark mb-4">
                  Whether you're looking to take your first art class, browse our gallery, or shop for supplies,
                  we'd love to welcome you to The Salty Mermaid family!
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <a href="/classes" className="btn btn-primary btn-lg">Browse Classes</a>
                  <a href="/contact" className="btn btn-success btn-lg">Get in Touch</a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default BootstrapAbout